import i18next, { TOptions } from "i18next";
import { TranslationKey, translationResources } from "translations";
import React, {
	useContext,
	createContext,
	useState,
	useEffect,
	FC,
	useRef,
} from "react";
import moment from "moment";
import "moment/locale/de";
import { devLog } from "utils/developmentLogger";

interface TranslationContextValue {
	t: (key: TranslationKey, options?: TOptions) => string;
}

const TranslationContext = createContext<TranslationContextValue>(null as any);

export const TranslationProvider: FC = (props) => {
	const [isInitialized, setIsInitialized] = useState(false);
	const instanceRef = useRef(i18next.createInstance());

	useEffect(() => {
		init();
	}, []);

	async function init() {
		try {
			const locale = navigator.language || "en_US";
			const language = locale.substring(0, 2);

			await instanceRef.current.init({
				lng: language,
				fallbackLng: "en",
				resources: {
					en: {
						translation: translationResources.en
					},
					de: {
						translation: translationResources.de
					}
				},
			});

			moment.locale(locale);
			moment.updateLocale(locale, { week: { dow: 1 } });
			setIsInitialized(true);
		} catch (error) {
			devLog.error("Translation initialization failed:", error);
		}
	}

	const value: TranslationContextValue = {
		t: (key: TranslationKey, options?: TOptions) => {
			return instanceRef.current.t(key, options);
		},
	};

	return (
		<TranslationContext.Provider value={value}>
			{isInitialized ? props.children : undefined}
		</TranslationContext.Provider>
	);
};

export function useTranslation() {
	return useContext(TranslationContext);
}
